import { TreatmentsContainerTreatment } from "./TreatmentsContainerTreatment.csharp";
import { TreatmentsContainerLocation } from "./TreatmentsContainerLocation.csharp";

interface SmartToken {
  Value: string;
  Score: number;
  Boosted: boolean;
}

interface SmartSearchItem {
  Item: any;
  Score: number;
}

const getSmartTokens = (treatment: TreatmentsContainerTreatment) => {
  let name: SmartToken = {
    Value: treatment.name.toLowerCase(),
    Score: 5,
    Boosted: false,
  };

  let keywords: SmartToken[] = treatment.keywords.map<SmartToken>((kw) => ({
    Value: kw.toLowerCase(),
    Score: 1,
    Boosted: false,
  }));

  let boostedKeywords: SmartToken[] = treatment.boostedKeywords.map<SmartToken>(
    (bkw) => ({
      Value: bkw.toLowerCase(),
      Score: 1,
      Boosted: true,
    })
  );

  return keywords.concat(boostedKeywords).concat([name]);
};

const calculateScoreForSmartTokens = (
  treatment: TreatmentsContainerTreatment,
  smartTokens: SmartToken[]
) => {
  const isAnyItemBoosted: number = smartTokens.some((t) => t.Boosted) ? 1 : 0;
  const smartBoostMultiplier: number =
    1000 * treatment.smartSearchBoost * isAnyItemBoosted;
  const sumOfFoundTokens: number = smartTokens
    .map((t) => t.Score)
    .reduce((a, b) => a + b, 0);

  return smartBoostMultiplier + sumOfFoundTokens;
};

const getSmartTokensForTreatmentByKeyword = (
  treatment: TreatmentsContainerTreatment,
  keyword: string
) => {
  const keywordLowerCase: string = keyword.toLowerCase();

  return getSmartTokens(treatment).filter((f) =>
    f.Value.includes(keywordLowerCase)
  );
};

const orderTreatmentsByIndexSort = (
  treatments: TreatmentsContainerTreatment[],
  keyword: string
) => {
  let dict: SmartSearchItem[] = [];

  treatments.forEach((t) => {
    let scores: number[] = [];

    const searchItemsForTreatment: SmartToken[] =
      getSmartTokensForTreatmentByKeyword(t, keyword);
    scores.push(calculateScoreForSmartTokens(t, searchItemsForTreatment));

    t.subtreatments.forEach((st) => {
      const searchItemsForTreatment: SmartToken[] =
        getSmartTokensForTreatmentByKeyword(st, keyword);
      scores.push(calculateScoreForSmartTokens(st, searchItemsForTreatment));
    });

    const sumScore: number = scores.reduce((a, b) => a + b, 0);

    dict.push({
      Item: t,
      Score: sumScore,
    });
  });

  dict.sort((a, b) => (a.Score > b.Score ? -1 : 1));
  dict.forEach(
    (e) =>
      (e.Item.subtreatments = orderTreatmentsByIndexSort(
        e.Item.subtreatments,
        keyword
      ))
  );

  return dict.map<TreatmentsContainerTreatment>((t) => t.Item);
};

const WithKeyword = (
  treatment: TreatmentsContainerTreatment,
  keyword: string
) => {
  const keywordLowerCase: string = keyword.toLowerCase();

  return (
    treatment.name.toLowerCase().includes(keywordLowerCase) ||
    treatment.keywords.some((kw) =>
      kw.toLowerCase().includes(keywordLowerCase)
    ) ||
    treatment.boostedKeywords.some((bkw) =>
      bkw.toLowerCase().includes(keywordLowerCase)
    )
  );
};

export const filterByLocations = (
  treatments: TreatmentsContainerTreatment[],
  selectedLocations: TreatmentsContainerLocation[],
  allLocationsSelected: boolean
) => {
  if (selectedLocations.length == 0) {
    return [];
  } else if (allLocationsSelected) {
    return treatments;
  } else {
    const locId = selectedLocations[0].locationId;
    const sameLocation = selectedLocations.every(
      (loc) => loc.locationId === locId
    );
    const flatLocationTreatments = selectedLocations.flatMap(
      (l) => l.flatAvailableTreatments
    );

    if (sameLocation) {
      return treatments
        .filter((spec) => flatLocationTreatments.some((s) => s.id === spec.id))
        .map((treatment) => ({
          ...treatment,
          name: flatLocationTreatments.find((t) => t.id == treatment.id)!.name,
          url: flatLocationTreatments.find((t) => t.id == treatment.id)!.url,
          subtreatments: treatment.subtreatments
            .filter((sub) => flatLocationTreatments.some((t) => t.id == sub.id))
            .map((subtreatment) => ({
              ...subtreatment,
              name: flatLocationTreatments.find((t) => t.id == subtreatment.id)!
                .name,
              url: flatLocationTreatments.find((t) => t.id == subtreatment.id)!
                .url,
            })),
        }));
    } else {
      return treatments
        .filter((treatment) =>
          flatLocationTreatments.some((t) => t.id === treatment.id)
        )
        .map((treatment) => ({
          ...treatment,
          subtreatments: treatment.subtreatments.filter((subtreatment) =>
            flatLocationTreatments.some((t) => t.id == subtreatment.id)
          ),
        }));
    }
  }
};

export const filterUnavailableOnLocations = (
  locations: TreatmentsContainerLocation[],
  treatments: TreatmentsContainerTreatment[]
) => {
  if (locations.length > 0) {
    const flatLocationTreatments = locations.flatMap(
      (l) => l.flatAvailableTreatments
    );

    return treatments
      .filter((spec) =>
        spec.subtreatments.some(
          (sub) => flatLocationTreatments.some((t) => t.id == sub.id) == false
        )
      )
      .map((treatment) => ({
        ...treatment,
        subtreatments: treatment.subtreatments.filter(
          (sub) => flatLocationTreatments.some((t) => t.id == sub.id) == false
        ),
      }));
  }
  return [];
};

export const filterByKeyword = (
  treatments: TreatmentsContainerTreatment[],
  keyword: string
) => {
  if (!keyword) {
    return treatments;
  } else {
    const filteredTreatments: TreatmentsContainerTreatment[] = treatments
      .filter(
        (sp) =>
          WithKeyword(sp, keyword) ||
          sp.subtreatments.some((service) => WithKeyword(service, keyword))
      )
      .map<TreatmentsContainerTreatment>((spec) => ({
        id: spec.id,
        name: spec.name,
        url: spec.url,
        smartSearchBoost: spec.smartSearchBoost,
        keywords: spec.keywords,
        boostedKeywords: spec.boostedKeywords,
        subtreatments: WithKeyword(spec, keyword)
          ? spec.subtreatments
          : spec.subtreatments.filter((s) => WithKeyword(s, keyword)),
      }));

    return orderTreatmentsByIndexSort(filteredTreatments, keyword);
  }
};
